import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Sparkles, Copy, Check, Loader2, Users, Truck, FileText, Megaphone, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Super-admin-only "Seed sample tenant" — spins up a fully populated demo
 * tenant (company + admin + 2 dispatchers + 5 drivers + 10 loads + invoices +
 * announcements) for sales demos and internal walkthroughs.
 */
const SeedSampleTenant = () => {
  const [seeding, setSeeding] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState({});

  const runSeed = async () => {
    setSeeding(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/superadmin/seed-sample-tenant`, {}, { withCredentials: true });
      setResult(res.data);
      toast.success('Demo tenant created');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const copy = async (key, value) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
        setCopied({ ...copied, [key]: true });
        setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 1500);
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch {
      toast.info(`Copy manually: ${value}`);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-3xl mx-auto" data-testid="seed-tenant-page">
        <header className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-8 h-8 text-[#002FA7]" />
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Seed Sample Tenant
            </h1>
          </div>
          <p className="text-slate-600">Spin up a fully-loaded demo company for sales walk-throughs.</p>
        </header>

        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
          <p className="text-sm font-medium text-slate-800 mb-3">Each seed tenant includes:</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-[#002FA7]" /> 1 company</div>
            <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#002FA7]" /> 1 admin + 2 dispatchers</div>
            <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#002FA7]" /> 5 drivers, 10 loads</div>
            <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-[#002FA7]" /> 2 invoices</div>
            <div className="flex items-center gap-2"><Megaphone className="w-4 h-4 text-[#002FA7]" /> 2 announcements</div>
            <div className="flex items-center gap-2 text-slate-500"><span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded">is_demo</span> flag</div>
          </div>
        </div>

        <button
          data-testid="seed-tenant-run-btn"
          onClick={runSeed}
          disabled={seeding}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-white transition bg-[#002FA7] hover:bg-[#00247D] disabled:bg-slate-300"
        >
          {seeding ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
          {seeding ? 'Provisioning demo tenant…' : 'Seed a new demo tenant'}
        </button>

        {result && (
          <div className="mt-6 bg-white border-2 border-emerald-200 rounded-xl p-6 shadow-sm" data-testid="seed-tenant-result">
            <div className="flex items-center gap-2 mb-4">
              <Check className="w-6 h-6 text-emerald-600" />
              <h3 className="font-bold text-slate-900 text-lg">Tenant ready</h3>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Company</p>
              <p className="text-lg font-semibold text-slate-900" data-testid="seed-tenant-company-name">{result.company.name}</p>
              <p className="text-xs text-slate-500 font-mono">{result.company.id}</p>
            </div>

            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-4">
              <p className="text-xs font-bold uppercase tracking-wide text-amber-800 mb-3">Admin login credentials</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="text-sm font-mono truncate" data-testid="seed-tenant-admin-email">{result.admin_credentials.email}</p>
                  </div>
                  <button
                    onClick={() => copy('email', result.admin_credentials.email)}
                    data-testid="seed-tenant-copy-email"
                    className="p-2 hover:bg-white rounded"
                  >
                    {copied.email ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-600" />}
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-slate-500">Password</p>
                    <p className="text-sm font-mono" data-testid="seed-tenant-admin-password">{result.admin_credentials.password}</p>
                  </div>
                  <button
                    onClick={() => copy('pw', result.admin_credentials.password)}
                    data-testid="seed-tenant-copy-password"
                    className="p-2 hover:bg-white rounded"
                  >
                    {copied.pw ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-600" />}
                  </button>
                </div>
              </div>
              <p className="text-xs text-amber-800 mt-3 italic">{result.admin_credentials.note}</p>
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-slate-50 border border-slate-200 rounded-lg py-3">
                <p className="text-2xl font-bold text-slate-900">{result.counts.users}</p>
                <p className="text-xs text-slate-500 uppercase">Users</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg py-3">
                <p className="text-2xl font-bold text-slate-900">{result.counts.loads}</p>
                <p className="text-xs text-slate-500 uppercase">Loads</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg py-3">
                <p className="text-2xl font-bold text-slate-900">{result.counts.drivers}</p>
                <p className="text-xs text-slate-500 uppercase">Drivers</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SeedSampleTenant;
